/**
 * Filtering for the client lists.
 *
 * A single bar sits above the table — search, then a Filters button that folds
 * the rest open in place, directly above the rows it narrows. It was a popup
 * for a while, which kept the page from moving but put the controls on a layer
 * over the very list they were changing: you set a filter, dismissed the sheet
 * to see what it did, and reopened it to adjust. Inline costs a little vertical
 * space and gives that feedback back. Search stays on the bar either way,
 * because it is the one control used constantly.
 *
 * The dropdown options come from the data itself rather than the settings
 * lists, so a value that only exists on imported rows is still filterable.
 *
 * Sorting is not here. The table's own column headers already sort, and a
 * second control for it invited the two to disagree about what the list was
 * ordered by.
 */
import { useRef, useState } from 'react';
import { useApp } from '../store/AppContext.jsx';
import Icon from '../lib/icons.jsx';
import { pad2 } from '../lib/dates.js';
import {
  PROMO_ANY, PROMO_NONE, PRESET_FLAGS } from '../constants/defaults.js';
import {
  CAME_ANY, CAME_SELF, SEEN_WINDOWS, SEEN_COLD, SEEN_NEVER, PARTNER_ANY, PARTNER_NONE
} from '../domain/derive.js';
import { readClientCSV, exportClientsCSV } from '../lib/csv.js';

/* The list pages carry their own toolbar on this bar: the card/table switch
   and the CSV pair. Both act on the list directly below, so they belong beside
   the control that decides what is in it rather than up in the page header. */
const LIST_VIEWS = ['clients', 'trials', 'payments', 'renewals'];

/*
 * The tile presets, as controls rather than as a state you can only reach by
 * clicking a tile and only leave by clearing every filter you have.
 *
 * They cannot be dropdowns. "Active" is three statuses, "Unpaid" is live and
 * not paid and past a date it should have been - conditions across several
 * columns, not a value in one. Writing them as a Status or Payment selection
 * would put a number in the panel that disagreed with the tile above it.
 */
const VIEWS = [
  ['All clients', ''],
  ['Active', 'all-live'],
  ['In trial', 'in-trial'],
  ['Unpaid', 'unpaid'],
  ['Renewing', 'renewing'],
  ['No recent contact', 'stale']
];

const DROPDOWNS = [
  ['Type', 'star', 'acctType'],
  ['Status', 'flag', 'status'],
  ['Plan', 'tag', 'plan'],
  ['Payment', 'card', 'pay'],
  ['Source', 'zap', 'source'],
  ['Promo', 'tag', 'promo'],
  /* Who brought them in. Empty until an account carries one. */
  ['Partner', 'users', 'partner'],
  /* How the account got here. Only ever has anything in it once somebody has
     carried a lead or a deal across — see optionsFor. */
  /* When they last signed in. Windows rather than dates — see optionsFor. */
  ['Last login', 'clock', 'seen'],
  ['Came from', 'refresh', 'cameFrom'],
  ['Owner', 'users', 'handler']
];

const iso = d => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/**
 * The ranges people actually ask for, each resolved against the current date
 * at click time rather than stored as a mode — so a window left open overnight
 * cannot go on claiming "today" about yesterday.
 */
const RANGES = [
  ['Today', () => { const n = new Date(); return [n, n]; }],
  ['Yesterday', () => {
    const y = new Date(); y.setDate(y.getDate() - 1); return [y, y];
  }],
  ['This month', () => {
    const n = new Date();
    return [new Date(n.getFullYear(), n.getMonth(), 1), n];
  }],
  ['Last month', () => {
    const n = new Date();
    return [new Date(n.getFullYear(), n.getMonth() - 1, 1),
      new Date(n.getFullYear(), n.getMonth(), 0)];
  }],
  ['This year', () => {
    const n = new Date();
    return [new Date(n.getFullYear(), 0, 1), n];
  }]
];

export default function FilterCard({ view }) {
  const {
    S, rows, derive, filters, setFilters, flag, setFlag, runSmartSearch, resetFilters,
    mode, setMode, patchSettings, visible, importClients, toast
  } = useApp();

  const [ask, setAsk] = useState('');
  const [open, setOpen] = useState(false);
  const fileInput = useRef(null);

  const showTools = LIST_VIEWS.includes(view);

  /* What is actually narrowing the list: the tile the user picked, or failing
     that the preset the page was opened with. */
  const effective = flag || PRESET_FLAGS[view] || '';

  const chooseMode = m => { setMode(m); patchSettings({ startMode: m }); };

  const onImportFile = file => {
    const reader = new FileReader();
    reader.onload = () => {
      const { records, error } = readClientCSV(reader.result);
      if (error) { toast(error); return; }
      importClients(records);
    };
    reader.onerror = () => toast('That file could not be read');
    reader.readAsText(file);
  };

  const onExport = () => {
    const n = exportClientsCSV(visible(view), r => derive(r).type, S.dateFmt);
    toast(`Exported ${n} rows`);
  };

  /* The button counts everything the panel holds, search included — the box
     moved inside, so with the panel shut this badge is the only sign that a
     search is narrowing the list. A date window is one filter however many of
     its two ends are set. */
  const active = Object.entries(filters)
    .filter(([k, v]) => !['from', 'to'].includes(k) && v).length
    + (flag ? 1 : 0)
    + (filters.from || filters.to ? 1 : 0);

  /** Distinct values present in the data for one column. */
  const optionsFor = key => {
    /*
     * Promo is not a column of a dozen values like the rest. Three hundred
     * and fifty-nine distinct codes is a dropdown nobody can scroll, and the
     * ones used once are not a channel — they are a code somebody generated
     * for one customer. So: the two questions that are always worth asking,
     * then only the codes that were used more than once, busiest first.
     */
    if (key === 'promo') {
      const n = new Map();
      rows.forEach(r => {
        const code = String(r.promo || '').trim().toUpperCase();
        if (code) n.set(code, (n.get(code) || 0) + 1);
      });
      const shared = [...n.entries()]
        .filter(([, count]) => count > 1)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([code, count]) => [code, `${code} (${count})`]);
      return [[PROMO_ANY, 'On any code'], [PROMO_NONE, 'On no code'], ...shared];
    }

    /*
     * Three answers, not a list of values.
     *
     * Nearly every account registered itself and carries nothing here, so the
     * distinct values are 'deal' and 'pipeline' and a great deal of nothing.
     * What people ask is "everything somebody moved across" first, and which
     * board second — and the counts are on the options because the size of
     * each pile is the answer to the manager's actual question.
     */
    /*
     * Windows, not values.
     *
     * "When did they last sign in" is always asked as "in the last N days",
     * and a list of eleven hundred distinct dates answers nothing. The counts
     * ride on the options because the size of each band is the answer: the
     * gap between "signed in this week" and "signed in this quarter" is the
     * whole health of the book on one line.
     */
    /*
     * Partners, busiest first, with the two questions that are always worth
     * asking above them. Empty when nobody came through one, so the control
     * is not drawn at all — see the guard where DROPDOWNS is rendered.
     */
    if (key === 'partner') {
      const n = new Map();
      rows.forEach(r => {
        const who = String(r.partner || '').trim();
        if (who) n.set(who, (n.get(who) || 0) + 1);
      });
      if (!n.size) return [];
      const through = [...n.values()].reduce((t, c) => t + c, 0);
      const direct = rows.length - through;
      return [
        [PARTNER_ANY, `Through a partner (${through})`],
        ...(direct ? [[PARTNER_NONE, `Direct, no partner (${direct})`]] : []),
        ...[...n.entries()]
          .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
          .map(([who, c]) => [who, `${who} (${c})`])
      ];
    }

    if (key === 'seen') {
      const days = r => derive(r).dAct;
      /* A length, not a distance back — the same day the filter itself
         counts. See seenWithin. */
      const within = n => rows.filter(x => {
        const v = days(x);
        return v !== null && v <= Math.max(0, n - 1);
      }).length;
      const never = rows.filter(x => days(x) === null).length;
      const cold = rows.filter(x => { const v = days(x); return v !== null && v > 60; }).length;
      return [
        ...SEEN_WINDOWS.map(n => [String(n), `Last ${n} days (${within(n)})`]),
        ...(cold ? [[SEEN_COLD, `Not in 60 days (${cold})`]] : []),
        ...(never ? [[SEEN_NEVER, `Never signed in (${never})`]] : [])
      ];
    }

    if (key === 'cameFrom') {
      const n = { deal: 0, pipeline: 0 };
      rows.forEach(r => { const v = String(r.cameFrom || ''); if (n[v] !== undefined) n[v] += 1; });
      const moved = n.deal + n.pipeline;
      if (!moved) return [];
      /* Both halves, because "show me only the ones we carried across" and
         "show me only the ones who signed themselves up" are the same
         question asked from either end, and the second was unanswerable. */
      return [
        [CAME_ANY, `Moved here by us (${moved})`],
        ...(n.deal ? [['deal', `From deals (${n.deal})`]] : []),
        ...(n.pipeline ? [['pipeline', `From the pipeline (${n.pipeline})`]] : []),
        [CAME_SELF, `Registered in the app (${rows.length - moved})`]
      ];
    }

    const set = new Set();
    rows.forEach(r => {
      const v = key === 'acctType' ? derive(r).type : String(r[key] || '').trim();
      if (v) set.add(v);
    });
    /* Every other column is its own label. */
    return [...set].sort().map(v => [v, v]);
  };

  const setRange = fn => {
    const [a, b] = fn();
    setFilters(f => ({ ...f, from: iso(a), to: iso(b) }));
  };

  const clearAll = () => {
    resetFilters();
    setAsk('');
  };

  /* Which preset, if any, the current window happens to be — so the chosen one
     reads as chosen, and a hand-edited date quietly deselects all of them. */
  const activeRange = RANGES.find(([, fn]) => {
    const [a, b] = fn();
    return filters.from === iso(a) && filters.to === iso(b);
  });

  return (
    <>
      <div className="fbar">
        <button
          type="button"
          className={`btn${active ? ' primary' : ''}`}
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
        >
          <Icon name="filter" />Filters
          {active ? <span className="fcount">{active}</span> : null}
          <span className={`fchev${open ? ' up' : ''}`}><Icon name="chev" /></span>
        </button>

        {active ? (
          <button type="button" className="btn" onClick={clearAll}>
            <Icon name="refresh" />Clear
          </button>
        ) : null}

        <div className="fspace" />

        {showTools ? (
          <>
            <div className="seg">
              <button type="button" className={mode === 'grid' ? 'on' : ''}
                onClick={() => chooseMode('grid')} title="Cards">
                <Icon name="grid" />
              </button>
              <button type="button" className={mode === 'list' ? 'on' : ''}
                onClick={() => chooseMode('list')} title="Table">
                <Icon name="list" />
              </button>
            </div>

            <button type="button" className="btn" onClick={() => fileInput.current?.click()}>
              <Icon name="up" />Import
            </button>
            <button type="button" className="btn" onClick={onExport}>
              <Icon name="down" />Export
            </button>

            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              ref={fileInput}
              onChange={e => { const f = e.target.files[0]; if (f) onImportFile(f); e.target.value = ''; }}
            />
          </>
        ) : null}
      </div>

      {open ? (
        <div className="card fpanel">
          <div className="fgrid">
            {/* What the tiles set, shown where the rest of the filters are.
                On Trials, Payments and Renewals the page itself carries a
                preset, so the chip it amounts to is marked too - otherwise the
                panel read "All clients" over a list that was nothing of the
                sort. Those pages drop the "All clients" chip: it would set an
                empty flag, the page preset would take over again, and the list
                would not move. */}
            <div className="fld spanall">
              <label><Icon name="filter" />View</label>
              <div className="franges">
                {VIEWS
                  .filter(([, f]) => f || !PRESET_FLAGS[view])
                  .map(([label, f]) => (
                    <button
                      key={label}
                      type="button"
                      className={`frange${effective === f ? ' on' : ''}`}
                      onClick={() => setFlag(flag === f ? '' : f)}
                    >
                      {label}
                    </button>
                  ))}
              </div>
            </div>

            {/* Search leads the panel. It is the control reached for first, so
                it keeps the first slot rather than being ordered after the
                dropdowns it is usually used instead of. */}
            <div className="fld span2">
              <label><Icon name="search" />Search</label>
              <div className="srch">
                <Icon name="search" />
                <input
                  placeholder="Name, email, promo, owner, notes…"
                  value={filters.q}
                  onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
                />
                {filters.q ? (
                  <button
                    type="button"
                    className="iconbtn sm"
                    aria-label="Clear the search"
                    onClick={() => setFilters(f => ({ ...f, q: '' }))}
                  >
                    <Icon name="x" />
                  </button>
                ) : null}
              </div>
            </div>

            {DROPDOWNS.map(([label, icon, key]) => {
              const opts = optionsFor(key);
              /* A dropdown whose only entry is "All" asks a question with one
                 answer. Came-from is empty until somebody has actually
                 carried a row across, and until then it is not a control. */
              if (!opts.length) return null;
              return (
              <div className="fld" key={key}>
                <label><Icon name={icon} />{label}</label>
                <select
                  value={filters[key]}
                  onChange={e => setFilters(f => ({ ...f, [key]: e.target.value }))}
                >
                  <option value="">All {label.toLowerCase()}</option>
                  {opts.map(([v, text]) => (
                    <option key={v} value={v}>{text}</option>
                  ))}
                </select>
              </div>
              );
            })}
          </div>

          {/* Both ends read the registration date, and both are inclusive. The
              cross on a label drops that end alone: a native date input has no
              way back to empty once a date is in it, and the one on the bar
              clears every filter, not just this pair. */}
          <div className="fdates">
            <div className="fld">
              <label>
                <Icon name="cal" />Registered from
                {filters.from ? (
                  <button
                    type="button"
                    className="fclear"
                    aria-label="Clear the start date"
                    title="Clear the start date"
                    onClick={() => setFilters(f => ({ ...f, from: '' }))}
                  >
                    <Icon name="x" />
                  </button>
                ) : null}
              </label>
              <input
                type="date"
                value={filters.from}
                max={filters.to || undefined}
                onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
              />
            </div>

            <div className="fld">
              <label>
                <Icon name="cal" />Registered to
                {filters.to ? (
                  <button
                    type="button"
                    className="fclear"
                    aria-label="Clear the end date"
                    title="Clear the end date"
                    onClick={() => setFilters(f => ({ ...f, to: '' }))}
                  >
                    <Icon name="x" />
                  </button>
                ) : null}
              </label>
              <input
                type="date"
                value={filters.to}
                min={filters.from || undefined}
                onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
              />
            </div>

            <div className="fld span2">
              <label><Icon name="zap" />Quick range</label>
              <div className="franges">
                {RANGES.map(([name, fn]) => (
                  <button
                    key={name}
                    type="button"
                    className={`frange${activeRange && activeRange[0] === name ? ' on' : ''}`}
                    onClick={() => setRange(fn)}
                  >
                    {name}
                  </button>
                ))}
                {filters.from || filters.to ? (
                  <button
                    type="button"
                    className="frange clear"
                    onClick={() => setFilters(f => ({ ...f, from: '', to: '' }))}
                  >
                    <Icon name="x" />Clear dates
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="fgrid pt0">
            <div className="fld span2">
              <label><Icon name="zap" />Ask AI</label>
              <div className="srch">
                <Icon name="zap" />
                <input
                  placeholder="e.g. unpaid GROWTH clients owned by Sana"
                  value={ask}
                  onChange={e => setAsk(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); runSmartSearch(ask); }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
